import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { useMemoFormContext } from '../context/FormContext';
import TitleField from '../fields/TitleField';
import DescriptionField from '../fields/DescriptionField';
import { MemoType } from 'models/Memo';
import { requireContentForType } from '../utils/validators';
import { useTranslations } from 'next-intl';
import { hostOfCompleteUrl } from 'utils/UrlUtils';
import { Field } from 'elements/Field';
import { useLinkPreviewUrl } from '../hooks/useLinkPreviewUrl';
import { LinkPreviewCard } from './LinkPreviewCard';

// The link composer: one address field with a Paste button, and once the
// address looks complete, a card with the site's preview from cortex.
const LinkForm: React.FC = () => {
  const t = useTranslations();
  const {
    control,
    isTitleVisible,
    isDescriptionVisible,
    getValues,
    setValue,
    watch,
  } = useMemoFormContext();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const content = (watch('content') as string | undefined) || '';
  const host = hostOfCompleteUrl(content);
  const previewUrl = useLinkPreviewUrl(content);

  // Reading the clipboard needs a secure context and browser support.
  const canPaste =
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.readText === 'function';

  const handlePaste = async () => {
    try {
      const text = (await navigator.clipboard.readText()).trim();
      if (text) {
        setValue('content', text, { shouldValidate: true, shouldDirty: true });
      }
    } catch {
      // The browser refused (permission denied or a paste prompt was
      // dismissed). The field still works with a keyboard paste.
    }
    inputRef.current?.focus();
  };

  return (
    <div>
      {isTitleVisible && <TitleField />}
      {isDescriptionVisible && <DescriptionField />}

      <Controller
        control={control}
        name="content"
        render={({ field: { onChange, value, onBlur, ref } }) => (
          <Field.Root className={styles.field.root}>
            <FontAwesomeIcon
              icon={faLink}
              className={styles.field.icon}
              aria-hidden="true"
            />
            <Field.Input
              id="content"
              name="content"
              type="url"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              ref={(element) => {
                ref(element);
                inputRef.current = element;
              }}
              value={value || ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder={t('memoComposer.link.placeholder')}
              className={styles.field.input}
            />
            {/* Only while the field is empty: once an address is in, the
                button would just be in the way. */}
            {canPaste && !value && (
              <button
                type="button"
                onClick={handlePaste}
                className={styles.field.paste}
              >
                {t('memoComposer.link.paste')}
              </button>
            )}
          </Field.Root>
        )}
        rules={{
          validate: requireContentForType(
            getValues,
            MemoType.Link,
            t('memoComposer.contentRequired'),
          ),
        }}
      />

      {host && <LinkPreviewCard host={host} previewUrl={previewUrl} />}
    </div>
  );
};

const styles = {
  field: {
    // A touch shorter than a settings field, to sit in the composer.
    root: 'px-3 text-sm',
    input: 'py-2',
    icon: 'shrink-0 text-[13px] text-ink-tertiary',
    paste: classNames(
      'shrink-0 rounded-lg border border-stroke bg-surface-card px-2.5 py-1 text-xs font-semibold text-ink-primary',
      'hover:bg-ink-secondary/10 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-ink/40',
    ),
  },
};

export default LinkForm;
